/**
 * US state, territory, Canadian province, and armed forces address validation.
 * Includes abbreviation lookup and full name resolution.
 */

// Declare types

// Constants
const UPPERALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// Module-level state variables
let STATES: Record<string, string> | null = null;
let ABREV_LOOKUP: Record<string, string> = {};
let NON_CONTIGUOUS: Record<string, boolean> = {};
let TERRITORIES: Record<string, boolean> = {};
let CANADA: Record<string, boolean> = {};
let ARMED_FORCES: Record<string, boolean> = {};

/**
 * Extracts characters from string that match the mask
 */
function extract(str: string, mask: string): string {
  let out = "";
  for (let i = 0; i < str.length; i++) {
    if (mask.indexOf(str.charAt(i)) != -1) {
      out += str.charAt(i);
    }
  }
  return out;
}

/**
 * Creates a Set-like object from arguments
 */
function makeSet(...args: string[]): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const arg of args) {
    out[arg] = true;
  }
  return out;
}

/**
 * Adds a state to the lookup tables
 */
function addState(abrev: string, fullName: string, ...lookups: string[]): void {
  STATES![abrev] = fullName;
  for (const lookup of lookups) {
    ABREV_LOOKUP[lookup] = abrev;
  }
}

/**
 * Initializes all state lookup tables
 */
function initLists(): void {
  STATES = {};
  ABREV_LOOKUP = {};
  NON_CONTIGUOUS = makeSet("AK", "HI");
  TERRITORIES = makeSet("AS", "FM", "GU", "MH", "NP", "PW", "PR", "UM", "VI");
  CANADA = makeSet("AB", "BC", "MB", "NB", "NF", "NS", "NT", "ON", "PE", "QC", "SK", "YT");
  ARMED_FORCES = makeSet("AA", "AE", "AP");

  addState("AB", "Alberta", "ALB");
  addState("AK", "Alaska", "ALAS");
  addState("AL", "Alabama", "ALAB");
  addState("AR", "Arkansas", "ARK");
  addState("AS", "American Samoa", "AME", "SAM");
  addState("AZ", "Arizona", "ARI");
  addState("BC", "British Columbia", "BRI");
  addState("CA", "California", "CAL");
  addState("CO", "Colorado", "COL");
  addState("CT", "Connecticut", "CON");
  addState("DC", "District of Columbia", "DIS");
  addState("DE", "Delaware", "DEL");
  addState("FM", "Federated States of Micronesia", "FED", "MICR");
  addState("FL", "Florida", "FLO");
  addState("GA", "Georgia", "GEO");
  addState("GU", "Guam", "GUA");
  addState("HI", "Hawaii", "HAW");
  addState("IA", "Iowa", "IOW");
  addState("ID", "Idaho", "IDA");
  addState("IL", "Illinois", "ILL");
  addState("IN", "Indiana", "IND");
  addState("KS", "Kansas", "KAN");
  addState("KY", "Kentucky", "KEN");
  addState("LA", "Louisianna", "LOU");
  addState("MH", "Marshall Islands", "MARS");
  addState("MA", "Massachusetts", "MAS");
  addState("MB", "Manitoba", "MAN");
  addState("MD", "Maryland", "MARY");
  addState("ME", "Maine", "MAI");
  addState("MI", "Michigan", "MICH");
  addState("MN", "Minnesota", "MINN");
  addState("MO", "Missouri", "MISSO");
  addState("MP", "Northern Mariana Islands", "NORTHE", "MARI");
  addState("MS", "Mississippi", "MISSI");
  addState("MT", "Montana", "MON");
  addState("NB", "New Brunswick", "NEWB");
  addState("NE", "Nebraska", "NEB");
  addState("NF", "Newfoundland", "NEWF");
  addState("NH", "New Hampshire", "NEWH");
  addState("NJ", "New Jersey", "NEWJ");
  addState("NM", "New Mexico", "NEWM");
  addState("NC", "North Carolina", "NCA", "NORTHC");
  addState("ND", "North Dakota", "NDA", "NORTHD");
  addState("NS", "Nova Scotia", "NOV");
  addState("NT", "Nunavut", "NUN");
  addState("NV", "Nevada", "NEV");
  addState("NY", "New York", "NEWY");
  addState("OH", "Ohio", "OHI");
  addState("OK", "Oklahoma", "OKL");
  addState("ON", "Ontario", "ONT");
  addState("OR", "Oregon", "ORE");
  addState("PW", "Palau", "PAL");
  addState("PA", "Pennsylvania", "PEN");
  addState("PE", "Prince Edward Island", "PRI");
  addState("PR", "Puerto Rico", "PUE");
  addState("QC", "Quebec", "QUE");
  addState("RI", "Rhode Island", "RHO");
  addState("SC", "South Carolina", "SCA", "SOUTHC");
  addState("SD", "South Dakota", "SDA", "SOUTHD");
  addState("SK", "Saskatchewan", "SAS");
  addState("TN", "Tennessee", "TEN");
  addState("TX", "Texas", "TEX");
  addState("UT", "Utah", "UTA");
  addState("VA", "Virginia", "VIRGINIA");
  addState("VI", "Virgin Islands", "VIRGINIS");
  addState("VT", "Vermont", "VER");
  addState("WA", "Washington", "WAS");
  addState("WI", "Wisconsin", "WIS");
  addState("WV", "West Virginia", "WES", "WVI");
  addState("WY", "Wyoming", "WYO");
  addState("YT", "Yukon", "YUK");
  addState("AA", "Armed Forces Americas", "AFAM");
  addState("AE", "Armed Forces Europe", "AFAF", "AFC", "AFE", "AFM");
  addState("AP", "Armed Forces Pacific", "AFP");
  addState("UM", "U.S. Minor Outlying Islands", "USM", "OUT", "MINO");

  // Sync to globalThis for backwards compatibility
  (globalThis as Record<string, unknown>).STATES = STATES;
  (globalThis as Record<string, unknown>).ABREV_LOOKUP = ABREV_LOOKUP;
  (globalThis as Record<string, unknown>).NON_CONTIGUOUS = NON_CONTIGUOUS;
  (globalThis as Record<string, unknown>).TERRITORIES = TERRITORIES;
  (globalThis as Record<string, unknown>).CANADA = CANADA;
  (globalThis as Record<string, unknown>).ARMED_FORCES = ARMED_FORCES;
}

/**
 * Finds the state abbreviation from a value (name or abbreviation)
 */
function findAbrev(value: string): string | null {
  if (STATES == null) initLists();

  value = extract(value.toUpperCase(), UPPERALPHA);
  let len = value.length;
  if (len < 2) return null;

  if (len == 2) {
    if (STATES![value] != null) return value;
    return null;
  }

  if (value.substring(0, 11) == "ARMEDFORCES") {
    value = "AF" + value.substring(11);
  }
  len = value.length;
  const maxLen = len < 8 ? len : 8;
  let state: string | null = null;
  for (let i = 3; i <= maxLen; i++) {
    state = ABREV_LOOKUP[value.substring(0, i)] || null;
    if (state != null) {
      if (state == "WA" && value.indexOf("DC") != -1) {
        return "DC";
      }
      return state;
    }
  }
  return null;
}

/**
 * Interface for validation form element
 */
interface StateValidationElement {
  abrev: string | { set: (val: string) => void; executeEvent: (event: string) => void } | null;
  fullName: boolean | undefined;
  all: boolean | undefined;
  territories: boolean | undefined;
  canadian: boolean | undefined;
  armedForces: boolean | undefined;
  states: boolean | undefined;
  contiguous: boolean | undefined;
  nonContiguous: boolean | undefined;
  get: () => string;
  set: (val: string) => void;
  setMessage: (msg: string) => boolean;
  getSibling: (name: string) => { set: (val: string) => void; executeEvent: (event: string) => void };
}

/**
 * Main state validation function
 */
function validateState(this: StateValidationElement): boolean {
  const abrev = findAbrev(this.get());
  if (this.abrev != null) {
    let abrevField = this.abrev;
    if (typeof abrevField === "string") {
      abrevField = this.getSibling(abrevField);
    }
    (abrevField as { set: (val: string) => void; executeEvent: (event: string) => void }).set(abrev == null ? "" : abrev);
    (abrevField as { set: (val: string) => void; executeEvent: (event: string) => void }).executeEvent("change");
  }
  if (abrev == null) {
    return this.setMessage("Please enter the name or postal abbreviation of a state, province, or U.S. territory.");
  }
  this.set(this.fullName ? STATES![abrev] : abrev);
  if (this.all === true) return true;
  if (TERRITORIES[abrev]) {
    return this.setMessage(this.territories === true ? "" : "U.S. territories are not permitted.");
  }
  if (CANADA[abrev]) {
    return this.setMessage(this.canadian === true ? "" : "Canadian provinces are not permitted.");
  }
  if (ARMED_FORCES[abrev]) {
    return this.setMessage(this.armedForces === true ? "" : "Armed forces addresses are not permitted.");
  }
  if (this.states === false || (this.contiguous === false && this.nonContiguous === false)) {
    return this.setMessage("U.S. states are not permitted");
  }
  if (NON_CONTIGUOUS[abrev]) {
    return this.setMessage(this.nonContiguous !== false ? "" : "Alaska and Hawaii are not permitted.");
  }
  return this.setMessage(this.contiguous !== false ? "" : "The 48 contiguous states and D.C. are not permitted");
}

// Getter functions to access module state (for tests and external use)
function getSTATES(): Record<string, string> | null {
  return STATES;
}

function getABREV_LOOKUP(): Record<string, string> {
  return ABREV_LOOKUP;
}

function getNUM_LOOKUP(): Record<string, number> | null {
  // This was in the original test but not in the original JS - return null for compatibility
  return null;
}

// Export for module usage
export {
  validateState,
  findAbrev,
  initLists,
  addState,
  makeSet,
  extract,
  UPPERALPHA,
  getSTATES,
  getABREV_LOOKUP,
  getNUM_LOOKUP,
  // Export state variables directly for test access
  STATES,
  ABREV_LOOKUP,
  NON_CONTIGUOUS,
  TERRITORIES,
  CANADA,
  ARMED_FORCES
};

// Expose on globalThis for backwards compatibility
(globalThis as Record<string, unknown>).validateState = validateState;
(globalThis as Record<string, unknown>).findAbrev = findAbrev;
(globalThis as Record<string, unknown>).initLists = initLists;
(globalThis as Record<string, unknown>).addState = addState;
(globalThis as Record<string, unknown>).makeSet = makeSet;

// Script resource notification
if (typeof _SR_ !== 'undefined' && _SR_ !== null) {
  _SR_.notify("validatestate.js");
}
